/**
 * API-lager för inloggningsbilder.
 *
 * Publik del (inloggningssidan, före login): hämtar dagens bild utan cookies.
 * Admin-del (Organisationsinställningar): lista, ladda upp, slå av/på, ta bort.
 * Alla admin-anrop går via apiFetch som lägger på tenant-header och cookies.
 */
import { resolveApiUrl } from '../../apiBase';
import { apiFetch, getActiveTenantSlug } from '../../api';
import { felmeddelande } from '../../utils/felmeddelanden.js';

const ADMIN_BASE = '/tenants/current/login-images';

async function readErrorMessage(res, fallback) {
  try {
    const data = await res.json();
    if (data && data.detail) {
      return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    }
  } catch {
    /* svaret var inte JSON */
  }
  // Statuskoden hor inte hemma i text som nar en anvandare.
  return felmeddelande(res.status, null, fallback);
}

/**
 * Dagens inloggningsbild för besökarens organisation, eller null om ingen är
 * aktiverad (då visar inloggningssidan standardgrafiken).
 * @returns {Promise<{id: string, url: string, width?: number, height?: number} | null>}
 */
export async function fetchTodaysLoginImage() {
  const api = resolveApiUrl();
  let slug = '';
  try {
    slug = String(getActiveTenantSlug() || '').trim();
  } catch {
    slug = '';
  }
  const qs = slug ? `?tenant_slug=${encodeURIComponent(slug)}` : '';
  const res = await fetch(`${api}/public/login-images${qs}`, { method: 'GET' });
  if (!res.ok) return null;
  const data = await res.json();
  if (!data || !data.image || !data.image.url) return null;
  return { ...data.image, url: `${api}${data.image.url}` };
}

export async function listLoginImages() {
  const res = await apiFetch(ADMIN_BASE);
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Kunde inte hämta inloggningsbilder'));
  return res.json();
}

export async function uploadLoginImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiFetch(ADMIN_BASE, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Uppladdningen misslyckades'));
  return res.json();
}

export async function setLoginImageActive(imageId, active) {
  const res = await apiFetch(`${ADMIN_BASE}/${encodeURIComponent(imageId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active: Boolean(active) }),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Kunde inte uppdatera bilden'));
  return res.json();
}

export async function deleteLoginImage(imageId) {
  const res = await apiFetch(`${ADMIN_BASE}/${encodeURIComponent(imageId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Kunde inte ta bort bilden'));
  return res.json();
}

/** Hämtar en skyddad bildfil (miniatyr eller fullbild) som Blob för förhandsvisning i admin. */
export async function fetchLoginImageBlob(path) {
  const res = await apiFetch(path);
  if (!res.ok) throw new Error(felmeddelande(res.status, null, 'Kunde inte hämta bilden.'));
  return res.blob();
}
